"use client"

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react"
import dynamic from "next/dynamic"
import Image from "next/image"
import Link from "next/link"
import type { Route } from "next"
import { usePathname } from "next/navigation"
import { Globe, X } from "lucide-react"
import { useTranslations } from "next-intl"

import { WatchLibraryIcon } from "@/components/watch/watch-section-styles"

import type {
  FloatingSearchControllerProps,
  PendingSearchSubmitIntent,
} from "./FloatingSearchController"
import { FloatingSearchBar } from "./FloatingSearchBar"
import { SearchOverlayInstantShell } from "./SearchOverlayInstantShell"
import {
  FloatingSearchPinnedContext,
  type FloatingSearchPinnedContextValue,
  WatchRouteSurfaceContext,
  type WatchRouteSurface,
  type WatchRouteSurfaceContextValue,
} from "./FloatingSearchContext"
import { AccountControl } from "@/components/watch/AccountControl"
import { WatchRoleChip } from "@/components/watch/WatchRoleChip"
import { useWatchAudiencePresence } from "@/components/watch/watch-audience-presence"
import type { GlobalLanguagePickerModalProps } from "@/components/watch/GlobalLanguagePickerModal"
import {
  WATCH_MODAL_CLOSE_DELAY_MS,
  useWatchModalActivity,
} from "@/components/watch/WatchModalActivityProvider"
import {
  FLOATING_HEADER_FIELD_WIDTH_CLASS,
  FLOATING_HEADER_HOME_LOGO_SLOT_CLASS,
  FLOATING_HEADER_LAYOUT_CLASS,
  FLOATING_HEADER_LANGUAGE_SLOT_CLASS,
  FLOATING_HEADER_LOGO_SLOT_CLASS,
  FLOATING_HEADER_PINNED_TOP_CLASS,
  FLOATING_HEADER_TOP_CLASS,
  FLOATING_HEADER_TRAILING_GROUP_CLASS,
  FLOATING_HEADER_TRAILING_SLOT_CLASS,
  FLOATING_MODAL_HEADER_CLOSE_POSITION_CLASS,
  FLOATING_MODAL_HEADER_FIELD_POSITION_CLASS,
  FLOATING_MODAL_HEADER_LANGUAGE_POSITION_CLASS,
  FLOATING_MODAL_HEADER_LAYOUT_CLASS,
  FLOATING_MODAL_HEADER_LOGO_POSITION_CLASS,
  FLOATING_MODAL_HEADER_TRAILING_GROUP_CLASS,
  WATCH_PAGE_LEFT_EDGE_CLASSES,
  WATCH_PAGE_RIGHT_EDGE_CLASSES,
} from "@/lib/content-width"
import { languageBadgeCodeFor } from "@/lib/language-code"
import {
  deriveLanguageDisplay,
  isolateLanguageName,
} from "@/lib/language-display"
import { isPublicWatchHomeLanguageSlug } from "@/lib/locale"
import {
  languageVideosIndexPath,
  localizedHomePath,
  parseWatchPath,
  tryAsLocaleSlug,
} from "@/lib/routes"
import { isOneSegmentCollectionSlug } from "@/lib/url-shape"
import {
  loadWatchInteraction,
  scheduleWatchInteractionWarmup,
} from "@/lib/watch-interaction-loader"
import {
  WATCH_HEADER_LANGUAGE_SWITCHER_EVENT,
  WATCH_PLAYER_CHROME_REVEAL_EVENT,
  WATCH_PLAYER_CHROME_VISIBILITY_EVENT,
  WATCH_PLAYER_PLAYBACK_STATE_EVENT,
  type WatchHeaderLanguageSwitcherDetail,
  type WatchPlayerChromeVisibilityDetail,
  type WatchPlayerPlaybackStateDetail,
} from "@/lib/watch-player-chrome-events"

export {
  useFloatingSearch,
  useFloatingSearchPinned,
} from "./FloatingSearchContext"
export type {
  FloatingSearchContextValue,
  FloatingSearchPinnedContextValue,
} from "./FloatingSearchContext"

const HEADER_HOVER_HEIGHT_PX = 144

const LazyFloatingSearchController = dynamic<FloatingSearchControllerProps>(
  () =>
    import("./FloatingSearchController").then((module) => ({
      default: module.FloatingSearchController,
    })),
  { ssr: false },
)

const LazyGlobalLanguagePickerModal = dynamic<GlobalLanguagePickerModalProps>(
  () =>
    import("@/components/watch/GlobalLanguagePickerModal").then((module) => ({
      default: module.GlobalLanguagePickerModal,
    })),
  { ssr: false },
)

type HeaderLanguageSwitcherState = {
  visible: boolean
  onClick: (() => void) | null
  languageCode: string | null
  languageName: string | null
  ownerToken: symbol | null
  pathname: string | null
}

type RouteIdentity = Readonly<{ pathname: string }>

type PendingPageLanguageOpen = {
  ownerToken: symbol | null
  onClick: () => void
  routeIdentity: RouteIdentity
}

type WatchRouteSurfaceRegistrationState = {
  pathname: string
  surface: WatchRouteSurface
  owner: symbol
}

type ParsedWatchPath = ReturnType<typeof parseWatchPath>

type HeaderFocusSource = "none" | "pointer" | "keyboard"

// Keys that only change a modifier state; pressing one is not keyboard use of
// the focused control.
const MODIFIER_ONLY_KEYS = new Set([
  "Alt",
  "AltGraph",
  "CapsLock",
  "Control",
  "Fn",
  "Meta",
  "NumLock",
  "ScrollLock",
  "Shift",
])

// `:focus-visible` is the browser's own keyboard-versus-pointer focus signal.
// Where it cannot be evaluated, treat focus as keyboard focus: showing the
// header is the safe direction for an accessibility affordance.
function isKeyboardFocus(target: EventTarget): boolean {
  if (!(target instanceof Element)) return true
  try {
    return target.matches(":focus-visible")
  } catch {
    return true
  }
}

function fallbackWatchRouteSurface(
  parsed: ParsedWatchPath,
): WatchRouteSurface | null {
  if (parsed.kind === "home") return "language-home"
  if (parsed.kind !== "localized-home") return null
  if (isPublicWatchHomeLanguageSlug(parsed.lang)) return "language-home"
  if (isOneSegmentCollectionSlug(parsed.lang)) return "experience"
  return "english-video"
}

function resolveCurrentWatchLanguageSlug(
  parsed: ParsedWatchPath,
  routeSurface: WatchRouteSurface | null,
  defaultLanguageSlug: string,
): string {
  if (parsed.kind === "video" || parsed.kind === "episode") return parsed.lang
  if (routeSurface === "english-video") return "english"
  if (routeSurface === "language-home" && parsed.kind === "localized-home") {
    return parsed.lang
  }
  return defaultLanguageSlug
}

function resolveHeaderLanguageSlug(
  parsed: ParsedWatchPath,
  currentLanguageSlug: string,
): string {
  if (
    parsed.kind === "language-videos" ||
    parsed.kind === "localized-languages" ||
    parsed.kind === "localized-history"
  ) {
    return parsed.lang
  }
  return currentLanguageSlug
}

export function FloatingSearchProvider({
  children,
  defaultLanguageSlug = "english",
  initialRouteSurface = null,
}: {
  children: ReactNode
  defaultLanguageSlug?: string
  initialRouteSurface?: WatchRouteSurface | null
}) {
  const t = useTranslations("FloatingSearch")
  const audiencePresence = useWatchAudiencePresence()
  const searchT = useTranslations("SearchOverlay")
  const languageT = useTranslations("LanguagePickerModal")
  const languageComboboxT = useTranslations("LanguageCombobox")
  const pathname = usePathname()
  const routeIdentity = useMemo<RouteIdentity>(() => ({ pathname }), [pathname])
  const parsedPath = useMemo(() => parseWatchPath(pathname), [pathname])
  const [initialRoute] = useState(() => ({
    pathname,
    surface: initialRouteSurface,
  }))
  const [routeSurfaceRegistration, setRouteSurfaceRegistration] =
    useState<WatchRouteSurfaceRegistrationState | null>(null)
  const unregisterRouteSurface = useCallback(
    (registrationPathname: string, owner: symbol) => {
      setRouteSurfaceRegistration((current) =>
        current?.pathname === registrationPathname && current.owner === owner
          ? null
          : current,
      )
    },
    [],
  )
  const registerRouteSurface = useCallback(
    (registrationPathname: string, surface: WatchRouteSurface) => {
      const owner = Symbol("watch-route-surface")
      setRouteSurfaceRegistration((current) => {
        const baselineSurface =
          registrationPathname === initialRoute.pathname &&
          initialRoute.surface != null
            ? initialRoute.surface
            : fallbackWatchRouteSurface(parseWatchPath(registrationPathname))

        // A clean registration that confirms the server seed or pathname
        // fallback does not need to force another pre-paint provider render.
        if (
          current?.pathname !== registrationPathname &&
          surface === baselineSurface
        ) {
          return null
        }

        return {
          pathname: registrationPathname,
          surface,
          owner,
        }
      })
      return () => unregisterRouteSurface(registrationPathname, owner)
    },
    [initialRoute, unregisterRouteSurface],
  )
  const initialSurface =
    initialRoute.pathname === pathname ? initialRoute.surface : null
  const routeSurface =
    routeSurfaceRegistration?.pathname === pathname
      ? routeSurfaceRegistration.surface
      : (initialSurface ?? fallbackWatchRouteSurface(parsedPath))
  const routeSurfaceValue = useMemo<WatchRouteSurfaceContextValue>(
    () => ({
      surface: routeSurface,
      register: registerRouteSurface,
    }),
    [registerRouteSurface, routeSurface],
  )
  const currentLanguageSlug = resolveCurrentWatchLanguageSlug(
    parsedPath,
    routeSurface,
    defaultLanguageSlug,
  )
  const headerLanguageSlug = resolveHeaderLanguageSlug(
    parsedPath,
    currentLanguageSlug,
  )
  const currentLanguageCodeSlug =
    languageBadgeCodeFor({ slug: headerLanguageSlug }) != null
      ? headerLanguageSlug
      : currentLanguageSlug
  const currentLanguageCode = languageBadgeCodeFor({
    slug: currentLanguageCodeSlug,
  })
  const currentLanguageName = deriveLanguageDisplay(
    currentLanguageCodeSlug,
    null,
  ).name
  // This is a client component, so it cannot await the route manifest the way
  // the inventory route's own admission does — the compiled corpus is the only
  // namespace it can see. The route itself no longer 404s outside that corpus,
  // so this veto now costs a language published since the last regeneration
  // its in-app entry point rather than preventing a dead link. That is a
  // degradation, which is the only shape a snapshot-only veto is allowed to
  // take; widening it needs a server-resolved slug passed down from the page.
  // Linking to the inventory from the page it already renders is noise, so the
  // control is absent rather than dead in that case too.
  const headerLanguageLocaleSlug = tryAsLocaleSlug(headerLanguageSlug)
  const languageVideosHref =
    parsedPath.kind !== "language-videos" &&
    headerLanguageLocaleSlug != null &&
    isPublicWatchHomeLanguageSlug(headerLanguageLocaleSlug)
      ? languageVideosIndexPath(headerLanguageLocaleSlug)
      : null
  const languageVideosLabel = languageT("seeAllVideosInLanguage", {
    language: isolateLanguageName(
      deriveLanguageDisplay(headerLanguageSlug, null).name,
    ),
  })
  const isWatchHome =
    routeSurface === "language-home" || routeSurface === "experience"
  const currentLocaleSlug = tryAsLocaleSlug(currentLanguageSlug)
  const logoHref = isWatchHome
    ? "https://www.jesusfilm.org/"
    : currentLocaleSlug && currentLocaleSlug !== "english"
      ? localizedHomePath(currentLocaleSlug)
      : "/"
  const logoSlotClass = isWatchHome
    ? FLOATING_HEADER_HOME_LOGO_SLOT_CLASS
    : FLOATING_HEADER_LOGO_SLOT_CLASS

  const [open, setOpenState] = useState<boolean>(false)
  const [closing, setClosing] = useState<boolean>(false)
  const [query, setQuery] = useState<string>("")
  const [searchControllerEnabled, setSearchControllerEnabled] = useState(false)
  const [searchControllerReady, setSearchControllerReady] = useState(false)
  const [searchResetToken, setSearchResetToken] = useState(0)
  const [pendingSearchSubmitIntent, setPendingSearchSubmitIntent] =
    useState<PendingSearchSubmitIntent | null>(null)
  const [pinned, setPinned] = useState<boolean>(false)
  const [playerChromeVisible, setPlayerChromeVisible] = useState(true)
  const [playerChromeOpacity, setPlayerChromeOpacity] = useState(1)
  const [playerPlaybackState, setPlayerPlaybackState] =
    useState<WatchPlayerPlaybackStateDetail>({
      playing: false,
      muted: true,
      preview: false,
    })
  const [headerLanguageSwitcher, setHeaderLanguageSwitcher] =
    useState<HeaderLanguageSwitcherState>({
      visible: false,
      onClick: null,
      languageCode: null,
      languageName: null,
      ownerToken: null,
      pathname: null,
    })
  const [globalLanguageOpenRoute, setGlobalLanguageOpenRoute] =
    useState<RouteIdentity | null>(null)
  const [globalLanguageLoadingRoute, setGlobalLanguageLoadingRoute] =
    useState<RouteIdentity | null>(null)
  const [globalLanguageErrorRoute, setGlobalLanguageErrorRoute] =
    useState<RouteIdentity | null>(null)
  const [headerHovered, setHeaderHovered] = useState(false)
  const [headerFocusSource, setHeaderFocusSource] =
    useState<HeaderFocusSource>("none")
  const [headerScrollVisible, setHeaderScrollVisible] = useState(true)
  const [headerOverHero, setHeaderOverHero] = useState(true)
  const closingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastScrollYRef = useRef(0)
  const globalLanguageIntentRef = useRef(0)
  const nextSearchSubmitIntentIdRef = useRef(0)
  const globalLanguagePendingRouteRef = useRef<RouteIdentity | null>(null)
  const globalLanguageTriggerRef = useRef<HTMLButtonElement>(null)
  const pendingPageLanguageOpenRef = useRef<PendingPageLanguageOpen | null>(
    null,
  )
  const pageLanguageCancellationRef = useRef<{
    ownerToken: symbol | null
    pathname: string
    cancel: () => void
  } | null>(null)
  useLayoutEffect(() => {
    if (pageLanguageCancellationRef.current?.pathname !== pathname) {
      pageLanguageCancellationRef.current = null
    }
  }, [pathname])
  const invalidateGlobalLanguageIntent = useCallback(() => {
    globalLanguageIntentRef.current += 1
    globalLanguagePendingRouteRef.current = null
    setGlobalLanguageLoadingRoute(null)
    setGlobalLanguageErrorRoute(null)
    setGlobalLanguageOpenRoute(null)
  }, [])

  const resetSearch = useCallback(() => {
    setQuery("")
    setPendingSearchSubmitIntent(null)
    setSearchResetToken((token) => token + 1)
  }, [])

  const setOpen = useCallback(
    (next: boolean) => {
      if (closingTimerRef.current) {
        clearTimeout(closingTimerRef.current)
        closingTimerRef.current = null
      }
      if (next) {
        pageLanguageCancellationRef.current?.cancel()
        pendingPageLanguageOpenRef.current = null
        invalidateGlobalLanguageIntent()
        setClosing(false)
        setOpenState(true)
      } else {
        resetSearch()
        setClosing(true)
        closingTimerRef.current = setTimeout(() => {
          setOpenState(false)
          setClosing(false)
          closingTimerRef.current = null
        }, WATCH_MODAL_CLOSE_DELAY_MS)
      }
    },
    [invalidateGlobalLanguageIntent, resetSearch],
  )

  useEffect(() => {
    return () => {
      globalLanguageIntentRef.current += 1
      globalLanguagePendingRouteRef.current = null
      if (closingTimerRef.current) clearTimeout(closingTimerRef.current)
    }
  }, [])

  useEffect(
    () =>
      scheduleWatchInteractionWarmup({
        globalLanguage: true,
        globalLanguageOptions: false,
        interactionKeys: [],
      }),
    [],
  )

  const enableSearchController = useCallback(() => {
    setSearchControllerEnabled(true)
    void loadWatchInteraction("search").catch(() => {})
  }, [])

  const openSearch = useCallback(() => {
    enableSearchController()
    setOpen(true)
  }, [enableSearchController, setOpen])

  const submitInstantSearch = useCallback((submittedQuery: string) => {
    if (submittedQuery.trim().length === 0) return
    nextSearchSubmitIntentIdRef.current += 1
    setPendingSearchSubmitIntent({
      id: nextSearchSubmitIntentIdRef.current,
      query: submittedQuery,
    })
  }, [])

  const markSearchControllerReady = useCallback(() => {
    setSearchControllerReady(true)
  }, [])

  useEffect(() => {
    if (!searchControllerEnabled) return
    void loadWatchInteraction("search").catch(() => {})
  }, [searchControllerEnabled])

  useEffect(() => {
    if (!open) return
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = ""
    }
  }, [open])

  // Scroll-driven pinned state. Shared between the floating searchbar and
  // the floating logo so they track together. Listener registers only while
  // modal is closed (body scroll lock keeps scrollY fixed while open).
  useEffect(() => {
    if (open) return
    if (typeof window === "undefined") return
    let frame = 0
    const updatePinned = () => {
      setPinned(window.scrollY > 80)
    }
    const onScroll = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        updatePinned()
        frame = 0
      })
    }
    updatePinned()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => {
      window.removeEventListener("scroll", onScroll)
      if (frame !== 0) window.cancelAnimationFrame(frame)
    }
  }, [open])

  useEffect(() => {
    if (open) return
    if (typeof window === "undefined") return

    let frame = 0
    const updateHeaderScrollState = () => {
      const currentY = window.scrollY
      const hero = document.querySelector<HTMLElement>(
        '[data-testid="hero-player-wrapper"], [data-testid="series-hero-static"], [data-testid="watch-home-tv-carousel"]',
      )
      const heroBottom = hero
        ? hero.offsetTop +
          Math.max(hero.offsetHeight, hero.getBoundingClientRect().height)
        : Math.max(window.innerHeight, 1)
      const overHero = currentY < heroBottom - 24
      const scrollingUp = currentY < lastScrollYRef.current - 4
      const scrollingDown = currentY > lastScrollYRef.current + 4

      setHeaderOverHero(overHero)
      if (overHero || scrollingUp || currentY < 24) {
        setHeaderScrollVisible(true)
      } else if (scrollingDown) {
        setHeaderScrollVisible(false)
      }

      lastScrollYRef.current = currentY
    }
    const onScroll = () => {
      if (frame !== 0) return
      frame = window.requestAnimationFrame(() => {
        updateHeaderScrollState()
        frame = 0
      })
    }

    updateHeaderScrollState()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
      if (frame !== 0) window.cancelAnimationFrame(frame)
    }
  }, [open, pathname])

  useLayoutEffect(() => {
    if (typeof window === "undefined") return

    const handleVisibilityChange = (event: Event) => {
      const detail = (event as CustomEvent<WatchPlayerChromeVisibilityDetail>)
        .detail
      if (typeof detail?.visible !== "boolean") return
      setPlayerChromeVisible(detail.visible)
      const nextOpacity =
        typeof detail.opacity === "number"
          ? Math.max(0, Math.min(1, detail.opacity))
          : detail.visible
            ? 1
            : 0
      setPlayerChromeOpacity(nextOpacity)
      if (nextOpacity < 1) setHeaderHovered(false)
    }

    window.addEventListener(
      WATCH_PLAYER_CHROME_VISIBILITY_EVENT,
      handleVisibilityChange,
    )
    return () => {
      window.removeEventListener(
        WATCH_PLAYER_CHROME_VISIBILITY_EVENT,
        handleVisibilityChange,
      )
    }
  }, [])

  useLayoutEffect(() => {
    if (typeof window === "undefined") return

    const handleLanguageSwitcherChange = (event: Event) => {
      const detail = (event as CustomEvent<WatchHeaderLanguageSwitcherDetail>)
        .detail
      if (typeof detail?.visible !== "boolean") return
      const ownerToken = detail.ownerToken ?? null
      const visible = detail.visible && typeof detail.onClick === "function"

      // A subtitle-only or fullscreen picker can own pending work without
      // replacing the header globe. Cleanup still belongs to its mount token.
      if (typeof detail.cancelPendingOpen === "function") {
        pageLanguageCancellationRef.current = {
          ownerToken,
          pathname,
          cancel: detail.cancelPendingOpen,
        }
      } else if (
        visible ||
        pageLanguageCancellationRef.current?.ownerToken === ownerToken
      ) {
        pageLanguageCancellationRef.current = null
      }

      setHeaderLanguageSwitcher((current) => {
        if (!visible) {
          const cleanupMatches = ownerToken
            ? current.ownerToken === ownerToken
            : current.ownerToken === null
          return cleanupMatches
            ? {
                visible: false,
                onClick: null,
                languageCode: null,
                languageName: null,
                ownerToken: null,
                pathname: null,
              }
            : current
        }

        return {
          visible: true,
          onClick: detail.onClick,
          languageCode: detail.languageCode ?? null,
          languageName: detail.languageName ?? null,
          ownerToken,
          pathname,
        }
      })
      if (visible) invalidateGlobalLanguageIntent()
    }

    window.addEventListener(
      WATCH_HEADER_LANGUAGE_SWITCHER_EVENT,
      handleLanguageSwitcherChange,
    )
    return () => {
      window.removeEventListener(
        WATCH_HEADER_LANGUAGE_SWITCHER_EVENT,
        handleLanguageSwitcherChange,
      )
    }
  }, [invalidateGlobalLanguageIntent, pathname])

  useLayoutEffect(() => {
    if (typeof window === "undefined") return

    const handlePlaybackStateChange = (event: Event) => {
      const detail = (event as CustomEvent<WatchPlayerPlaybackStateDetail>)
        .detail
      if (
        typeof detail?.playing !== "boolean" ||
        typeof detail?.muted !== "boolean"
      ) {
        return
      }
      setPlayerPlaybackState({
        playing: detail.playing,
        muted: detail.muted,
        preview: detail.preview === true,
      })
    }

    window.addEventListener(
      WATCH_PLAYER_PLAYBACK_STATE_EVENT,
      handlePlaybackStateChange,
    )
    return () => {
      window.removeEventListener(
        WATCH_PLAYER_PLAYBACK_STATE_EVENT,
        handlePlaybackStateChange,
      )
    }
  }, [])

  useLayoutEffect(() => {
    if (typeof window === "undefined") return
    const frame = window.requestAnimationFrame(() => {
      setPlayerChromeVisible(true)
      setPlayerChromeOpacity(1)
      setPlayerPlaybackState({ playing: false, muted: true, preview: false })
      setHeaderHovered(false)
      setHeaderScrollVisible(true)
      setHeaderOverHero(true)
      lastScrollYRef.current = window.scrollY
    })
    return () => window.cancelAnimationFrame(frame)
  }, [pathname])

  const globalLanguageOpen = globalLanguageOpenRoute === routeIdentity

  const openGlobalLanguage = useCallback(() => {
    if (
      globalLanguagePendingRouteRef.current === routeIdentity ||
      globalLanguageOpen
    ) {
      return
    }
    if (open || closing) setOpen(false)

    const intent = globalLanguageIntentRef.current + 1
    globalLanguageIntentRef.current = intent
    globalLanguagePendingRouteRef.current = routeIdentity
    setGlobalLanguageLoadingRoute(routeIdentity)
    setGlobalLanguageErrorRoute(null)

    void loadWatchInteraction("global-language")
      .then(() => {
        if (globalLanguageIntentRef.current === intent) {
          setGlobalLanguageOpenRoute(routeIdentity)
        }
      })
      .catch(() => {
        if (globalLanguageIntentRef.current === intent) {
          setGlobalLanguageErrorRoute(routeIdentity)
        }
      })
      .finally(() => {
        if (globalLanguageIntentRef.current === intent) {
          globalLanguagePendingRouteRef.current = null
          setGlobalLanguageLoadingRoute(null)
        }
      })
  }, [closing, globalLanguageOpen, open, routeIdentity, setOpen])

  const closeGlobalLanguage = useCallback(() => {
    setGlobalLanguageOpenRoute(null)
  }, [])

  const openPageSpecificLanguage = useCallback(() => {
    const onClick = headerLanguageSwitcher.onClick
    if (onClick == null) return
    if (!open && !closing) {
      onClick()
      return
    }

    pendingPageLanguageOpenRef.current = {
      ownerToken: headerLanguageSwitcher.ownerToken,
      onClick,
      routeIdentity,
    }
    setOpen(false)
  }, [closing, headerLanguageSwitcher, open, routeIdentity, setOpen])

  useEffect(() => {
    if (open || closing) return
    const pending = pendingPageLanguageOpenRef.current
    if (pending == null) return
    pendingPageLanguageOpenRef.current = null
    if (
      pending.routeIdentity !== routeIdentity ||
      !headerLanguageSwitcher.visible ||
      headerLanguageSwitcher.pathname !== pathname ||
      headerLanguageSwitcher.ownerToken !== pending.ownerToken ||
      headerLanguageSwitcher.onClick !== pending.onClick
    ) {
      return
    }
    pending.onClick()
  }, [closing, headerLanguageSwitcher, open, pathname, routeIdentity])

  const modalChromeHidden = open || closing
  const handleLogoClick = useCallback(
    (event: ReactMouseEvent<HTMLAnchorElement>) => {
      if (modalChromeHidden) {
        event.preventDefault()
        if (!closing) setOpen(false)
        return
      }
      resetSearch()
    },
    [closing, modalChromeHidden, resetSearch, setOpen],
  )
  // While search is open the logo acts as a close control (feat-302), so it is
  // exposed as a button named for that action rather than a Home link whose
  // name would disagree with what activating it does (QA N46). It stays the
  // same element so keyboard focus survives the close. Buttons also activate
  // on Space, which an anchor does not do natively.
  const handleLogoKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLAnchorElement>) => {
      if (!modalChromeHidden || event.key !== " ") return
      event.preventDefault()
      if (!closing) setOpen(false)
    },
    [closing, modalChromeHidden, setOpen],
  )
  useWatchModalActivity(modalChromeHidden, { releaseDelayMs: 0 })
  const playerPlayingWithSound =
    playerPlaybackState.playing && !playerPlaybackState.muted
  const pageSpecificLanguageSwitcherActive =
    headerLanguageSwitcher.pathname === pathname &&
    headerLanguageSwitcher.visible &&
    headerLanguageSwitcher.onClick != null
  const headerLanguageClick = pageSpecificLanguageSwitcherActive
    ? openPageSpecificLanguage
    : openGlobalLanguage
  const headerLanguageControlVisible = true
  const headerLanguageCode = pageSpecificLanguageSwitcherActive
    ? (headerLanguageSwitcher.languageCode ?? currentLanguageCode)
    : currentLanguageCode
  const headerLanguageBusy =
    !pageSpecificLanguageSwitcherActive &&
    globalLanguageLoadingRoute === routeIdentity
  const globalLanguageLoadFailed =
    !pageSpecificLanguageSwitcherActive &&
    globalLanguageErrorRoute === routeIdentity
  const globalLanguageName = !headerLanguageCode
    ? null
    : pageSpecificLanguageSwitcherActive
      ? (headerLanguageSwitcher.languageName ??
        (headerLanguageSwitcher.languageCode == null ||
        headerLanguageSwitcher.languageCode === currentLanguageCode
          ? currentLanguageName
          : headerLanguageCode))
      : currentLanguageName
  const globalLanguageDescription = headerLanguageCode
    ? globalLanguageName && globalLanguageName !== headerLanguageCode
      ? `${globalLanguageName} (${headerLanguageCode})`
      : headerLanguageCode
    : null
  // The page-specific switcher changes the playback (audio) language; the
  // global picker changes the site language and content, so it gets a
  // neutral name.
  const headerLanguageActionLabel = pageSpecificLanguageSwitcherActive
    ? t("changeAudioLanguage")
    : languageComboboxT("selectLanguage")
  const globalLanguageLabel = globalLanguageLoadFailed
    ? `${headerLanguageActionLabel}${globalLanguageDescription ? `: ${globalLanguageDescription}` : ""}. ${searchT("connectionHint")}`
    : `${headerLanguageActionLabel}${globalLanguageDescription ? `: ${globalLanguageDescription}` : ""}`
  const headerHoverZoneActive =
    !modalChromeHidden &&
    (playerPlayingWithSound || playerChromeOpacity < 1 || !playerChromeVisible)
  const effectiveHeaderHovered = headerHoverZoneActive && headerHovered
  // Keyboard focus inside the header keeps it revealed through every later
  // player-chrome fade and scroll-away until focus leaves, so a focused control
  // is never transparent or off-screen. Pointer-driven focus (a click) does not
  // pin the header, it only keeps the header from going inert under that focus.
  const headerKeyboardRevealed =
    !modalChromeHidden && headerFocusSource === "keyboard"
  const headerScrollRevealed = headerScrollVisible || headerKeyboardRevealed
  const headerChromeOpacity = headerKeyboardRevealed
    ? 1
    : effectiveHeaderHovered && playerChromeOpacity <= 0
      ? 1
      : playerChromeOpacity
  const headerChromeUnavailable = !modalChromeHidden && headerChromeOpacity <= 0
  const headerChromeHidden =
    headerChromeUnavailable || (!modalChromeHidden && !headerScrollRevealed)
  const headerChromeInert =
    headerChromeHidden &&
    headerFocusSource === "none" &&
    (!headerHoverZoneActive || !headerScrollRevealed)
  const headerChromeDimmed =
    !modalChromeHidden && !headerChromeHidden && headerChromeOpacity < 1
  const searchChromeVisible = !headerChromeUnavailable
  const searchChromeDimmed = headerChromeDimmed
  const headerCanBrightenLocally = playerChromeOpacity <= 0
  const headerPointerRevealAllowed = playerChromeOpacity < 1
  const headerSurfaceSolid = modalChromeHidden || !headerOverHero || pinned
  const headerTopClass = pinned
    ? FLOATING_HEADER_PINNED_TOP_CLASS
    : FLOATING_HEADER_TOP_CLASS
  const headerMotionClass = modalChromeHidden
    ? "pointer-events-none translate-y-0 opacity-100"
    : headerChromeUnavailable
      ? "pointer-events-none -translate-y-[calc(100%+2rem)] opacity-0"
      : !headerScrollRevealed
        ? "pointer-events-none -translate-y-[calc(100%+2rem)] opacity-100"
        : headerChromeDimmed
          ? "pointer-events-auto translate-y-0 opacity-30"
          : "pointer-events-auto translate-y-0 opacity-100"
  const headerBackdropMotionClass = headerChromeUnavailable
    ? "-translate-y-[calc(100%+2rem)] opacity-0"
    : !headerScrollRevealed
      ? "-translate-y-[calc(100%+2rem)] opacity-100"
      : headerSurfaceSolid
        ? "translate-y-0 opacity-100 md:-translate-y-[72%]"
        : "translate-y-0 opacity-100"

  const revealPlayerChromeFromHeader = useCallback(() => {
    if (typeof window === "undefined") return
    window.dispatchEvent(new CustomEvent(WATCH_PLAYER_CHROME_REVEAL_EVENT))
  }, [])

  const handleHeaderPointerEnter = useCallback(() => {
    if (!headerPointerRevealAllowed) return
    setHeaderHovered(headerCanBrightenLocally)
    revealPlayerChromeFromHeader()
  }, [
    headerCanBrightenLocally,
    headerPointerRevealAllowed,
    revealPlayerChromeFromHeader,
  ])

  const handleHeaderFocus = useCallback(
    (event: ReactFocusEvent<HTMLElement>) => {
      setHeaderFocusSource(
        isKeyboardFocus(event.target) ? "keyboard" : "pointer",
      )
      if (headerChromeHidden) {
        setHeaderHovered(headerCanBrightenLocally)
        revealPlayerChromeFromHeader()
      }
    },
    [
      headerCanBrightenLocally,
      headerChromeHidden,
      revealPlayerChromeFromHeader,
    ],
  )

  // Focus can stay on one control while the input modality changes: a control
  // focused by a click becomes keyboard-operated on its next key press (the
  // browser flips `:focus-visible` without a new focus event), and a
  // keyboard-focused control becomes pointer-operated on a press. Re-classify
  // on those inputs so the header reveal follows how it is actually used.
  const handleHeaderKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (MODIFIER_ONLY_KEYS.has(event.key)) return
      setHeaderFocusSource((current) =>
        current === "pointer" ? "keyboard" : current,
      )
    },
    [],
  )

  const handleHeaderPointerDown = useCallback(() => {
    setHeaderFocusSource((current) =>
      current === "keyboard" ? "pointer" : current,
    )
  }, [])

  const handleHeaderBlur = useCallback(
    (event: ReactFocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
        setHeaderFocusSource("none")
      }
    },
    [],
  )

  useEffect(() => {
    if (!headerHoverZoneActive) {
      return
    }

    const handlePointerMove = (event: PointerEvent) => {
      const hoveringHeader = event.clientY <= HEADER_HOVER_HEIGHT_PX
      setHeaderHovered(hoveringHeader && headerCanBrightenLocally)
      if (hoveringHeader && headerPointerRevealAllowed) {
        revealPlayerChromeFromHeader()
      }
    }

    window.addEventListener("pointermove", handlePointerMove)
    return () => window.removeEventListener("pointermove", handlePointerMove)
  }, [
    headerHoverZoneActive,
    headerCanBrightenLocally,
    headerPointerRevealAllowed,
    revealPlayerChromeFromHeader,
  ])

  const pinnedValue = useMemo<FloatingSearchPinnedContextValue>(
    () => ({
      pinned,
      playerChromeVisible,
      searchChromeVisible,
      searchChromeDimmed,
      searchOpen: modalChromeHidden,
    }),
    [
      pinned,
      playerChromeVisible,
      searchChromeVisible,
      searchChromeDimmed,
      modalChromeHidden,
    ],
  )

  return (
    <WatchRouteSurfaceContext.Provider value={routeSurfaceValue}>
      <FloatingSearchPinnedContext.Provider value={pinnedValue}>
        <div
          inert={modalChromeHidden || undefined}
          aria-hidden={modalChromeHidden || undefined}
          className={
            modalChromeHidden
              ? "blur-[12px] transition-[filter] duration-200"
              : "transition-[filter] duration-200"
          }
        >
          {children}
        </div>
        <div
          aria-hidden="true"
          data-testid="floating-header-backdrop"
          className={`pointer-events-none fixed inset-x-0 top-0 z-40 h-[calc(4.75rem+env(safe-area-inset-top,0px))] backdrop-blur-[14px] transition-[opacity,background-color,translate,backdrop-filter] duration-500 ease-[cubic-bezier(0.2,0.9,0.2,1)] md:h-[calc(8rem+env(safe-area-inset-top,0px))] md:backdrop-blur-none compact-landscape:h-[calc(4.25rem+env(safe-area-inset-top,0px))] compact-landscape:backdrop-blur-[14px] ${
            headerSurfaceSolid
              ? "bg-black/72 shadow-[0_1px_0_rgba(255,255,255,0.08)] md:bg-[linear-gradient(180deg,rgba(8,16,24,0.46)_0%,rgba(28,56,72,0.22)_44%,rgba(28,56,72,0.08)_72%,rgba(28,56,72,0)_100%)] md:shadow-none md:[mask-image:linear-gradient(to_bottom,black_0%,black_56%,transparent_100%)]"
              : "bg-[linear-gradient(180deg,rgba(8,16,24,0.46)_0%,rgba(28,56,72,0.22)_44%,rgba(28,56,72,0.08)_72%,rgba(28,56,72,0)_100%)] [mask-image:linear-gradient(to_bottom,black_0%,black_56%,transparent_100%)]"
          } ${headerBackdropMotionClass}`}
        />
        <div
          aria-hidden="true"
          data-testid="floating-header-hover-zone"
          onPointerEnter={handleHeaderPointerEnter}
          className={`fixed inset-x-0 top-0 z-[45] h-[calc(5.5rem+env(safe-area-inset-top,0px))] md:h-[calc(9rem+env(safe-area-inset-top,0px))] compact-landscape:h-[calc(4.25rem+env(safe-area-inset-top,0px))] ${
            headerHoverZoneActive
              ? "pointer-events-auto"
              : "pointer-events-none"
          }`}
        />
        <header
          data-testid="floating-header"
          inert={headerChromeInert || undefined}
          aria-hidden={headerChromeInert || undefined}
          onFocusCapture={handleHeaderFocus}
          onBlurCapture={handleHeaderBlur}
          onKeyDownCapture={handleHeaderKeyDown}
          onPointerDownCapture={handleHeaderPointerDown}
          className={`fixed ${WATCH_PAGE_LEFT_EDGE_CLASSES} ${WATCH_PAGE_RIGHT_EDGE_CLASSES} ${headerTopClass} z-50 ${
            modalChromeHidden
              ? FLOATING_MODAL_HEADER_LAYOUT_CLASS
              : `${FLOATING_HEADER_LAYOUT_CLASS} max-[360px]:gap-2`
          } transition-[top,opacity,translate] duration-500 ease-[cubic-bezier(0.2,0.9,0.2,1)] ${headerMotionClass}`}
        >
          <Link
            href={logoHref as Route}
            role={modalChromeHidden ? "button" : undefined}
            aria-label={modalChromeHidden ? t("closeSearch") : t("home")}
            data-testid="floating-header-logo"
            onClick={handleLogoClick}
            onKeyDown={handleLogoKeyDown}
            className={`pointer-events-auto flex ${logoSlotClass} ${
              modalChromeHidden ? FLOATING_MODAL_HEADER_LOGO_POSITION_CLASS : ""
            } items-center justify-start transition-opacity duration-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/80`}
          >
            <Image
              src={
                isWatchHome
                  ? "/watch/images/jesus-film-logo-full.svg"
                  : "/watch/images/jesusfilm-sign.svg"
              }
              alt="JesusFilm"
              width={isWatchHome ? 139 : 70}
              height={isWatchHome ? 36 : 70}
              unoptimized
              className={
                isWatchHome
                  ? "h-auto w-20 drop-shadow-md sm:w-28 md:w-[139px]"
                  : "h-auto max-w-[38px] drop-shadow-md sm:max-w-[50px] lg:max-w-[70px]"
              }
            />
          </Link>
          <div
            className={`${FLOATING_HEADER_FIELD_WIDTH_CLASS} ${
              modalChromeHidden
                ? `${FLOATING_MODAL_HEADER_FIELD_POSITION_CLASS} ${
                    headerLanguageControlVisible ? "" : "col-span-2"
                  }`
                : ""
            }`}
          >
            <FloatingSearchBar
              open={open}
              closing={closing}
              query={query}
              onOpen={openSearch}
            />
          </div>
          <div
            data-testid="floating-header-trailing-controls"
            className={
              modalChromeHidden
                ? FLOATING_MODAL_HEADER_TRAILING_GROUP_CLASS
                : `pointer-events-auto ${FLOATING_HEADER_TRAILING_GROUP_CLASS}`
            }
          >
            {!modalChromeHidden ? <WatchRoleChip /> : null}
            {!modalChromeHidden && languageVideosHref ? (
              <Link
                href={languageVideosHref}
                prefetch={false}
                data-testid="floating-header-language-videos-link"
                // No `aria-label`: the visible "Library" text is now the
                // accessible name. Overriding it with "See all videos in X"
                // would break WCAG 2.5.3 Label in Name, since the spoken name
                // would not contain the visible label. The richer phrasing
                // stays as the hover tooltip.
                title={languageVideosLabel}
                // Hidden below `md`: the header's trailing grid column is
                // `minmax(80px,1fr)` at mobile widths, so a third 44px control
                // pushes the language globe past the viewport edge (clipped by
                // `overflow-x-clip`). Widening that column is a header-wide
                // layout change, not a same-PR call.
                //
                // Absent entirely while the search modal is open at every
                // width: the overlay renders its own full-width row above the
                // category tiles instead, so the two never compete.
                // Auto width rather than the fixed square
                // FLOATING_HEADER_LANGUAGE_SLOT_CLASS, since the control now
                // carries a text label beside the glyph. Still hidden below
                // `md` for the reason above.
                className="pointer-events-auto hidden h-11 cursor-pointer items-center justify-center gap-2 rounded-full px-3 text-stone-100 transition-[color,transform] duration-300 ease-out hover:text-white focus-visible:ring-2 focus-visible:ring-stone-300 focus-visible:outline-none md:inline-flex md:h-[52px]"
              >
                <WatchLibraryIcon
                  aria-hidden
                  className="h-6 w-6 shrink-0 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.35)]"
                />
                {/* The label yields to the role chip between xl and 1700px. */}
                <span
                  className={`text-xs font-bold tracking-[0.14em] whitespace-nowrap uppercase drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.35)] sm:text-[10px]${audiencePresence ? " xl:hidden min-[1700px]:inline" : ""}`}
                >
                  {t("library")}
                </span>
              </Link>
            ) : null}
            {headerLanguageControlVisible ? (
              <button
                type="button"
                ref={globalLanguageTriggerRef}
                data-testid="floating-header-language-button"
                onClick={headerLanguageClick}
                aria-haspopup="dialog"
                aria-expanded={
                  pageSpecificLanguageSwitcherActive
                    ? undefined
                    : globalLanguageOpen
                }
                aria-busy={headerLanguageBusy}
                disabled={headerLanguageBusy}
                aria-label={globalLanguageLabel}
                title={globalLanguageLabel}
                className={`pointer-events-auto inline-flex ${FLOATING_HEADER_LANGUAGE_SLOT_CLASS} ${
                  modalChromeHidden
                    ? FLOATING_MODAL_HEADER_LANGUAGE_POSITION_CLASS
                    : ""
                } cursor-pointer items-center justify-center rounded-full text-stone-100 transition-[color,transform] duration-300 ease-out hover:text-white focus-visible:ring-2 focus-visible:ring-stone-300 focus-visible:outline-none ${
                  headerLanguageCode
                    ? `${modalChromeHidden ? "" : "-mr-[18.25px]"} w-auto min-w-[4.25rem] gap-1.5 px-2 md:w-auto md:min-w-[4.75rem]`
                    : ""
                }`}
              >
                <Globe
                  aria-hidden
                  className="h-6 w-6 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.35)]"
                />
                {headerLanguageCode ? (
                  <span
                    data-testid="floating-header-language-code"
                    className="max-w-28 truncate text-xs font-bold tracking-[0.14em] max-[360px]:max-w-12 max-[360px]:overflow-visible max-[360px]:break-words max-[360px]:text-clip max-[360px]:leading-3 max-[360px]:whitespace-normal sm:text-[10px]"
                  >
                    {headerLanguageCode}
                  </span>
                ) : null}
              </button>
            ) : null}
            {globalLanguageLoadFailed ? (
              <span
                role="status"
                data-testid="global-language-picker-load-error"
                className="sr-only"
              >
                {searchT("connectionHint")}
              </span>
            ) : null}
            {modalChromeHidden ? (
              <button
                type="button"
                aria-label={t("closeSearch")}
                data-testid="floating-header-search-close"
                onClick={() => setOpen(false)}
                className={`pointer-events-auto inline-flex ${FLOATING_HEADER_TRAILING_SLOT_CLASS} ${FLOATING_MODAL_HEADER_CLOSE_POSITION_CLASS} cursor-pointer items-center justify-center rounded-full text-stone-100 transition-[color,transform] duration-300 ease-out hover:text-white focus-visible:ring-2 focus-visible:ring-stone-300 focus-visible:outline-none`}
              >
                <X
                  aria-hidden
                  className="h-6 w-6 drop-shadow-[0_1px_1.5px_rgba(0,0,0,0.35)]"
                />
              </button>
            ) : (
              <AccountControl />
            )}
          </div>
        </header>
        {searchControllerEnabled ? (
          <LazyFloatingSearchController
            open={open}
            closing={closing}
            query={query}
            setOpen={setOpen}
            setQuery={setQuery}
            headerLanguageSwitcherVisible={headerLanguageControlVisible}
            headerLanguageCode={headerLanguageCode}
            headerPinned={pinned}
            languageVideosHref={languageVideosHref}
            resetToken={searchResetToken}
            pendingSubmitIntent={pendingSearchSubmitIntent}
            onReady={markSearchControllerReady}
          />
        ) : null}
        {modalChromeHidden && !searchControllerReady ? (
          <SearchOverlayInstantShell
            open={open}
            closing={closing}
            query={query}
            setOpen={setOpen}
            setQuery={setQuery}
            onSubmit={submitInstantSearch}
            headerTopClass={headerTopClass}
            logoSlotClass={logoSlotClass}
            headerLanguageControlVisible={headerLanguageControlVisible}
          />
        ) : null}
        {globalLanguageOpen && !modalChromeHidden ? (
          <LazyGlobalLanguagePickerModal
            open={globalLanguageOpen}
            pathname={pathname}
            currentLanguageSlug={headerLanguageSlug}
            onClose={closeGlobalLanguage}
            returnFocusRef={globalLanguageTriggerRef}
          />
        ) : null}
      </FloatingSearchPinnedContext.Provider>
    </WatchRouteSurfaceContext.Provider>
  )
}
